import { Link } from "react-router-dom";
import { Button } from "@/design-system/primitives";
import { useSupplementOrders } from "./api";
import { supplementPrice } from "./catalog";

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "Awaiting payment",
  queued: "Payment received · preparing order",
  submitted: "Preparing your order",
  placed: "Order placed",
  shipped: "Shipped",
  delivered: "Delivered",
  refund_pending: "Refund in progress",
  refunded: "Refunded",
  cancelled: "Cancelled",
  needs_attention: "Our team is reviewing your order",
};

/** History remains accessible from Orders even after the experiment is paused. */
export function SupplementOrders({
  highlightedOrderId,
  compact = false,
}: {
  highlightedOrderId?: string | null;
  compact?: boolean;
}) {
  const query = useSupplementOrders();
  if (query.isLoading)
    return compact ? null : (
      <p role="status" className="type-label-xs text-ink-secondary">
        Fetching supplement orders · · ·
      </p>
    );
  if (query.isError)
    return (
      <div className="border border-line-subtle p-15">
        <p role="alert" className="type-body-sm">
          We could not retrieve your supplement orders.
        </p>
        <Button
          variant="ghost"
          className="mt-10"
          onClick={() => void query.refetch()}
        >
          Try again
        </Button>
      </div>
    );
  const orders = query.data?.orders ?? [];
  if (!orders.length)
    return highlightedOrderId ? (
      <p role="status" className="p-15 border border-line-subtle type-body-sm">
        Your payment may still be processing. Refresh your orders shortly.
      </p>
    ) : null;
  const visibleOrders = compact ? orders.slice(0, 3) : orders;
  return (
    <section className="flex flex-col gap-10" aria-label="Supplement orders">
      <div className="flex items-center justify-between gap-10">
        <h2 className="type-label-sm">Supplement orders</h2>
        {compact && (
          <Link
            to="/app/orders"
            className="type-body-sm underline min-h-44 inline-flex items-center"
          >
            View all orders
          </Link>
        )}
      </div>
      {visibleOrders.map((order) => (
        <article
          key={order.id}
          className={`border p-15 ${order.id === highlightedOrderId ? "border-accent bg-accent-wash" : "border-line-subtle bg-page"}`}
        >
          <div className="flex items-start justify-between gap-15">
            <h3 className="type-body-md">{order.productTitle}</h3>
            <span className="type-label-sm tabular-nums shrink-0">
              {supplementPrice(order.totalCents)}
            </span>
          </div>
          <p className="type-body-sm text-ink-secondary mt-8">
            Quantity {order.quantity}
            {order.createdAt
              ? ` · ${new Date(order.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
              : ""}
          </p>
          <p className="type-label-xs mt-12" role="status">
            {STATUS_LABELS[order.status] ?? "Processing"}
          </p>
          {order.tracking?.map((shipment, index) => (
            <p
              key={`${shipment.trackingNumber}-${index}`}
              className="type-body-sm text-ink-secondary mt-10 break-all"
            >
              {shipment.carrier?.toLowerCase().includes("amazon")
                ? "Delivery partner"
                : shipment.carrier || "Carrier"} ·{" "}
              {shipment.trackingNumber || "Tracking pending"}
              {shipment.status
                ? ` · ${shipment.status.replaceAll("_", " ")}`
                : ""}
            </p>
          ))}
        </article>
      ))}
    </section>
  );
}
